import React, { createContext, useContext, ReactNode, useMemo, useState, useEffect } from 'react'; // Added useState, useEffect
import { getAuth, User as FirebaseUser } from 'firebase/auth';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDocumentData } from 'react-firebase-hooks/firestore';
import { doc, collection, query, where, documentId, getDocs } from 'firebase/firestore'; // Re-added imports for fetchPortfolios
import { db as firestoreInstance } from '@/utils/firebase';
import { useMultipleDocumentData } from './hooks/sync/useMultipleDocumentData'; // Corrected import path again
// UserData from context/types might need to be updated or this should import User from schemas/user
import { User as UserData } from '@/schemas/user'; // Assuming UserData should now be the Zod type with ID
import { userOutputSchema } from '@/schemas/user'; // Import userOutputSchema
// Import both Portfolio (with id) and portfolioSchema (data only)
import { Portfolio, portfolioSchema, portfolioOutputSchema } from '@/schemas/portfolio'; 
import { Business, businessSchema } from '@/schemas/business'; // Added businessSchema
import { z } from 'zod';
import { useUserOperations } from '@/context/schema/useUserOperations'; // Added
import { initializeUserData } from '@/utils/firestore/users/auth'; // Added

interface IFirebaseContext {
  firebaseUser: FirebaseUser | null | undefined;
  userData: UserData | null | undefined;
  portfolios: Portfolio[];
  businesses: Business[]; // Added
  isLoading: boolean;
  error: Error | undefined;
}

const FirebaseContext = createContext<IFirebaseContext | undefined>(undefined);

export const FirebaseProvider = ({ children }: { children: ReactNode }) => {
  const auth = getAuth();
  const [firebaseUser, loadingAuth, errorAuth] = useAuthState(auth);
  
  const userDocRef = firebaseUser ? doc(firestoreInstance, 'users', firebaseUser.uid) : null;
  // Not using transform here to avoid re-fetching on every minor change if schema is too strict.
  // Parsing will be done in useMemo.
  const [rawUserData, loadingUserDoc, errorUserDoc] = useDocumentData(userDocRef);

  // Memoize portfolioIds separately to ensure referential stability for the fetchPortfolios effect
  const portfolioIds = useMemo(() => {
    if (!rawUserData) {
      console.log("FirebaseProvider: portfolioIds useMemo - No rawUserData, returning [].");
      return [];
    }
    let ids;
    // Prioritize portfolioRefs, then portfolios
    if (typeof rawUserData.portfolioRefs === 'string') {
      try { 
        ids = JSON.parse(rawUserData.portfolioRefs); 
        console.log("FirebaseProvider: portfolioIds useMemo - Parsed portfolioRefs string:", ids);
      } catch (e) { 
        console.error('FirebaseProvider: portfolioIds useMemo - Failed to parse portfolioRefs string:', rawUserData.portfolioRefs, e); 
        ids = []; 
      }
    } else if (Array.isArray(rawUserData.portfolioRefs)) {
      ids = rawUserData.portfolioRefs;
      console.log("FirebaseProvider: portfolioIds useMemo - Used portfolioRefs array:", ids);
    } else if (typeof rawUserData.portfolios === 'string') {
      try { 
        ids = JSON.parse(rawUserData.portfolios); 
        console.log("FirebaseProvider: portfolioIds useMemo - Parsed portfolios string:", ids);
      } catch (e) { 
        console.error('FirebaseProvider: portfolioIds useMemo - Failed to parse portfolios string:', rawUserData.portfolios, e); 
        ids = []; 
      }
    } else if (Array.isArray(rawUserData.portfolios)) {
      ids = rawUserData.portfolios;
      console.log("FirebaseProvider: portfolioIds useMemo - Used portfolios array:", ids);
    } else {
      console.log("FirebaseProvider: portfolioIds useMemo - No portfolioRefs or portfolios field, returning [].");
      ids = [];
    }
    // Ensure it's always an array of strings, filtering out non-strings if necessary
    const finalIds = Array.isArray(ids) ? ids.filter(id => typeof id === 'string') : [];
    console.log("FirebaseProvider: portfolioIds useMemo - Final portfolio IDs:", finalIds);
    return finalIds;
  }, [rawUserData]);

  const userData = useMemo(() => {
    console.log("FirebaseProvider: userData useMemo - Raw user data received for UID:", firebaseUser?.uid, rawUserData);
    if (rawUserData) {
      try {
        // Start with a copy of rawUserData
        const dataToParse = { ...rawUserData } as any;

        // Replace portfolioRefs/portfolios with the memoized portfolioIds
        // This ensures that userData.portfolios uses the stable reference from the portfolioIds useMemo
        dataToParse.portfolios = portfolioIds;
        // It's good practice to remove the original string fields if the schema doesn't expect them,
        // or ensure the schema can handle them (e.g. with .optional() or .passthrough())
        // delete dataToParse.portfolioRefs; // If portfolioRefs is not in userOutputSchema

        // Handle permissions string parsing (if it's still an issue and needs similar memoization,
        // it would follow the same pattern as portfolioIds)
        if (typeof rawUserData.permissions === 'string') {
          console.log("FirebaseProvider: userData useMemo - user.permissions is a string. Attempting JSON.parse:", rawUserData.permissions);
          try {
            dataToParse.permissions = JSON.parse(rawUserData.permissions);
            console.log("FirebaseProvider: userData useMemo - Successfully parsed user.permissions:", dataToParse.permissions);
          } catch (parseError) {
            console.error("FirebaseProvider: userData useMemo - Failed to JSON.parse user.permissions string. Error:", parseError, "Original string:", rawUserData.permissions);
            // Keep original string in dataToParse.permissions if parsing fails, Zod will catch it if schema expects array
          }
        }

        // Handle businessRefs -> businesses mapping and parsing (similar pattern for memoization if needed)
        // For now, direct parsing within this useMemo. If business fetching loops, apply same pattern as portfolioIds.
        if (typeof rawUserData.businessRefs === 'string') {
          console.log("FirebaseProvider: userData useMemo - user.businessRefs is a string. Attempting JSON.parse:", rawUserData.businessRefs);
          try {
            dataToParse.businesses = JSON.parse(rawUserData.businessRefs);
          } catch (parseError) {
            console.error("FirebaseProvider: userData useMemo - Failed to JSON.parse user.businessRefs string. Error:", parseError);
            dataToParse.businesses = rawUserData.businessRefs; 
          }
        } else if (Array.isArray(rawUserData.businessRefs)) {
            dataToParse.businesses = rawUserData.businessRefs;
        } else if (typeof rawUserData.businesses === 'string') {
          console.log("FirebaseProvider: userData useMemo - user.businesses is a string. Attempting JSON.parse:", rawUserData.businesses);
          try {
            dataToParse.businesses = JSON.parse(rawUserData.businesses);
          } catch (parseError) {
            console.error("FirebaseProvider: userData useMemo - Failed to JSON.parse user.businesses string. Error:", parseError);
            dataToParse.businesses = rawUserData.businesses;
          }
        } else if (Array.isArray(rawUserData.businesses)) {
             dataToParse.businesses = rawUserData.businesses;
        }
        // delete dataToParse.businessRefs; // Optional

        // Ensure firebaseUser is not null when rawUserData exists (implies logged in)
        const uid = firebaseUser!.uid; 
        const finalDataToParse = { ...dataToParse, id: uid, userId: uid }; // Ensure id and userId are set for Zod schema

        console.log("FirebaseProvider: userData useMemo - Data before Zod parsing (userOutputSchema):", JSON.parse(JSON.stringify(finalDataToParse)));
        
        const parsedUser = userOutputSchema.parse(finalDataToParse) as UserData;
        console.log("FirebaseProvider: userData useMemo - Successfully Zod parsed user data for UID:", uid, parsedUser);
        return parsedUser;
      } catch (e) {
        if (e instanceof z.ZodError) {
          console.error("FirebaseProvider: userData useMemo - Zod validation error for user data. UID:", firebaseUser?.uid, "Issues:", e.issues, "Data attempted:", rawUserData, "Processed for parsing:", /*dataToParse - might be too verbose*/);
        } else {
          console.error("FirebaseProvider: userData useMemo - Error transforming user data. UID:", firebaseUser?.uid, "Error:", e, "Data attempted:", rawUserData);
        }
        return null;
      }
    }
    console.log("FirebaseProvider: userData useMemo - No rawUserData, returning null for userData. UID:", firebaseUser?.uid);
    return null;
  }, [rawUserData, firebaseUser?.uid, portfolioIds]); // Added portfolioIds to dependency array

  // Get the updateUser function from useUserOperations.
  // Pass `null` to make the updateUser function reference stable for the initializeUserData effect,
  // as it doesn't rely on the currentUserDataFromFirebase argument for its internal logic.
  // Moved this line after userData declaration
  const { updateUser } = useUserOperations(null);

  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [loadingPortfolios, setLoadingPortfolios] = useState(true);
  const [errorPortfolios, setErrorPortfolios] = useState<Error | undefined>(undefined);

  useEffect(() => {
    const fetchPortfolios = async () => {
      console.log("FirebaseProvider: fetchPortfolios effect called. FirebaseUser:", !!firebaseUser, "portfolioIds:", portfolioIds);
      // Use the memoized portfolioIds directly. userData might still be needed for other conditions or future use.
      if (!firebaseUser || !portfolioIds) { // Check portfolioIds directly
        console.log("FirebaseProvider: fetchPortfolios effect - No firebaseUser or no portfolioIds. Clearing portfolios.");
        setPortfolios([]);
        setLoadingPortfolios(false);
        return;
      }

      // const portfolioIds = userData.portfolios; // This line is now effectively replaced by the portfolioIds from useMemo
      console.log("FirebaseProvider: fetchPortfolios effect - Using memoized portfolioIds:", portfolioIds);

      if (portfolioIds.length > 0) { // Check length of the memoized portfolioIds
        try {
          setLoadingPortfolios(true);
          // Use the memoized portfolioIds for slicing, which should already be a valid array of strings
          const limitedPortfolioIds = portfolioIds.slice(0, 30); 
          console.log("FirebaseProvider: fetchPortfolios effect - Fetching portfolios for IDs (limited to 30):", limitedPortfolioIds);

          if (limitedPortfolioIds.length === 0) { // Should not happen if portfolioIds.length > 0, but good check
            console.log("FirebaseProvider: fetchPortfolios effect - No valid limitedPortfolioIds to fetch (unexpected). Clearing portfolios.");
            setPortfolios([]);
            setLoadingPortfolios(false);
            return;
          }
          
          const q = query(collection(firestoreInstance, 'portfolios'), where(documentId(), 'in', limitedPortfolioIds));
          const querySnapshot = await getDocs(q);
          console.log(`FirebaseProvider: fetchPortfolios effect - Fetched ${querySnapshot.docs.length} portfolio documents.`);

          const portfoliosData = querySnapshot.docs.map(docSnap => {
            const rawPortfolioData = docSnap.data();
            console.log("FirebaseProvider: fetchPortfolios effect - Raw data for portfolio ID:", docSnap.id, rawPortfolioData);
            try {
              const parsedPortfolio = portfolioOutputSchema.parse({ ...rawPortfolioData, id: docSnap.id }) as Portfolio;
              console.log("FirebaseProvider: fetchPortfolios effect - Successfully Zod parsed portfolio ID:", docSnap.id, parsedPortfolio);
              return parsedPortfolio;
            } catch (e) {
              if (e instanceof z.ZodError) {
                console.error("FirebaseProvider: fetchPortfolios effect - Zod validation error for portfolio. ID:", docSnap.id, "Issues:", e.issues, "Data:", rawPortfolioData);
              } else {
                console.error("FirebaseProvider: fetchPortfolios effect - Error transforming portfolio data. ID:", docSnap.id, "Error:", e, "Data:", rawPortfolioData);
              }
              return null;
            }
          }).filter(p => p !== null) as Portfolio[];
          
          console.log("FirebaseProvider: fetchPortfolios effect - Successfully fetched and parsed portfolios:", portfoliosData);
          setPortfolios(portfoliosData);
          setErrorPortfolios(undefined);
        } catch (err) {
          console.error("FirebaseProvider: fetchPortfolios effect - Error fetching portfolios by IDs:", err);
          setErrorPortfolios(err as Error);
          setPortfolios([]);
        } finally {
          setLoadingPortfolios(false);
        }
      } else {
        console.log("FirebaseProvider: fetchPortfolios effect - memoized portfolioIds is empty. Clearing portfolios.");
        setPortfolios([]);
        setLoadingPortfolios(false);
      }
    };

    // Condition to run fetchPortfolios
    if (!loadingAuth && !loadingUserDoc && firebaseUser) { // Ensure firebaseUser is present before fetching
      fetchPortfolios();
    } else if (!firebaseUser && !loadingAuth) { // If no user and auth is not loading, clear portfolios
        console.log("FirebaseProvider: fetchPortfolios effect - No firebaseUser and not loading auth. Clearing portfolios.");
        setPortfolios([]);
        setLoadingPortfolios(false);
    }
  }, [firebaseUser, portfolioIds, loadingAuth, loadingUserDoc]); // Changed dependency from userData to portfolioIds


  // Effect to initialize user data in Firestore when firebaseUser is available
  useEffect(() => {
    if (firebaseUser && updateUser) { // Ensure firebaseUser and updateUser are available
      console.log("FirebaseProvider: firebaseUser available, calling initializeUserData for UID:", firebaseUser.uid);
      initializeUserData(firebaseUser, updateUser)
        .then(() => {
          console.log("FirebaseProvider: initializeUserData completed for UID:", firebaseUser.uid);
        })
        .catch(err => {
          console.error("FirebaseProvider: Error calling initializeUserData for UID:", firebaseUser.uid, err);
        });
    } else if (!firebaseUser) {
      console.log("FirebaseProvider: No firebaseUser, skipping initializeUserData.");
    } else if (!updateUser) {
      console.warn("FirebaseProvider: firebaseUser present, but updateUser function is not yet available. Skipping initializeUserData for now. This might happen on initial load if useUserOperations depends on userData which is fetched after firebaseUser.");
    }
  }, [firebaseUser, updateUser]); // Depend on firebaseUser and updateUser

  // Derive business IDs from userData and portfolios for useMultipleDocumentData
  const businessIdsToFetch = useMemo(() => {
    if (!userData) {
      console.log("FirebaseProvider: businessIdsToFetch - No userData, returning empty array.");
      return [];
    }
    let allBusinessIds: string[] = [];
    if (userData.businesses && Array.isArray(userData.businesses)) {
      console.log("FirebaseProvider: businessIdsToFetch - Adding IDs from userData.businesses:", userData.businesses);
      allBusinessIds.push(...userData.businesses);
    }
    if (portfolios && portfolios.length > 0) {
      console.log("FirebaseProvider: businessIdsToFetch - Processing portfolios for businessRefs. Number of portfolios:", portfolios.length);
      portfolios.forEach(portfolio => {
        if (portfolio.businessRefs && Array.isArray(portfolio.businessRefs)) {
          console.log(`FirebaseProvider: businessIdsToFetch - Adding IDs from portfolio ${portfolio.id}:`, portfolio.businessRefs);
          allBusinessIds.push(...portfolio.businessRefs);
        }
      });
    } else {
      console.log("FirebaseProvider: businessIdsToFetch - No portfolios or empty portfolios array.");
    }
    const uniqueIds = Array.from(new Set(allBusinessIds.filter(id => !!id)));
    console.log("FirebaseProvider: businessIdsToFetch - Final unique IDs:", uniqueIds);
    return uniqueIds;
  }, [userData, portfolios]);

  // Use the real-time hook for businesses
  const { 
    data: businesses, 
    loading: loadingBusinessesRealtime, // Renamed to avoid conflict if we bring back parts of old loading
    error: errorBusinessesRealtime 
  } = useMultipleDocumentData<Business>('businesses', businessIdsToFetch, businessSchema);
  
  // The old useEffect for fetchBusinesses is now replaced by useMultipleDocumentData.
  // The pre-processing for gocampaign.services (if it's an array) should ideally be handled
  // within the Zod schema itself (e.g., using .preprocess or .transform) if it's a common data shape issue.
  // For now, useMultipleDocumentData handles parsing with the provided businessSchema.

  const isLoading = loadingAuth || loadingUserDoc || loadingPortfolios || loadingBusinessesRealtime;
  const error = errorAuth || errorUserDoc || errorPortfolios || errorBusinessesRealtime;

  // useEffect to log businesses when they change
  useEffect(() => {
    if (!isLoading && businesses) { // Log only when not loading and businesses data is available
      console.log("FirebaseProvider: Businesses data updated in context:", JSON.parse(JSON.stringify(businesses)));
    }
  }, [businesses, isLoading]);

  const contextValue = useMemo(() => ({
    firebaseUser,
    userData,
    portfolios,
    businesses, // Added
    isLoading,
    error,
  }), [firebaseUser, userData, portfolios, businesses, isLoading, error]); // Added businesses

  return (
    <FirebaseContext.Provider value={contextValue}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = (): IFirebaseContext => {
  const context = useContext(FirebaseContext);
  if (context === undefined) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};
